import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiLayers, FiShield, FiTruck } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { registerUserAccount } from "../../services/registerApi";
import "./RegisterForms.css";
import useCompanySuggestions from "./useCompanySuggestions";
import useReferralAttribution from "./useReferralAttribution";
import { buildReferralLink } from "../../utils/referralAttribution";
import { PHONE_COUNTRY_OPTIONS } from "../../constants/phoneCountryCodes";

const initialState = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  phoneCountry: PHONE_COUNTRY_OPTIONS[0].value,
  company: "",
  companyType: "",
  password: "",
  confirmPassword: "",
  taxId: "",
  referralCode: ""
};

const buildInitialState = referralCode => ({
  ...initialState,
  referralCode: referralCode || ""
});

const COMPANY_TYPE_OPTIONS = [
  { value: "ground_transport", label: "Transporte terrestre" },
  { value: "security_service", label: "Custodia" },
  { value: "suppliers", label: "Vendedor" },
  { value: "logistics_operator", label: "Freight forwarder" },
  { value: "client", label: "Cliente" },
  { value: "road_assistance", label: "Soporte vial" },
  { value: "customs_agent", label: "Agente aduanal" },
  { value: "news_agent", label: "Agente de noticias" }
];

const RegisterProvider = ({ onGoogleSignIn, googleSignInPending = false }) => {
  const { referral, clearReferral } = useReferralAttribution();
  const [values, setValues] = useState(() => buildInitialState(referral?.code));
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanyOwner, setIsCompanyOwner] = useState(false);
  const navigate = useNavigate();
  const trimmedCompany = values.company.trim();
  const hasExactMatch =
    selectedCompany &&
    trimmedCompany.toLowerCase() === (selectedCompany.name || "").toLowerCase();
  const { suggestions, loading } = useCompanySuggestions(values.company, {
    enabled: !submitting && !hasExactMatch
  });
  const showSuggestions = !selectedCompany && trimmedCompany.length >= 2;

  useEffect(() => {
    document.body.classList.add("hide-topbar");
    return () => document.body.classList.remove("hide-topbar");
  }, []);

  useEffect(() => {
    if (!referral?.code) {
      return;
    }
    setValues(prev =>
      prev.referralCode
        ? prev
        : {
            ...prev,
            referralCode: referral.code
          }
    );
  }, [referral?.code]);

  const handleFieldChange = field => event => {
    const { value } = event.target;
    setValues(prev => ({ ...prev, [field]: value }));
    if (field === "company") {
      setSelectedCompany(prev =>
        prev && value.trim().toLowerCase() === (prev.name || "").toLowerCase() ? prev : null
      );
    }
  };

  const handleSelectCompany = option => {
    if (!option) return;
    setSelectedCompany(option);
    setValues(prev => ({ ...prev, company: option.name || "" }));
  };

  const handleClearSelectedCompany = () => {
    setSelectedCompany(null);
  };

  const handlePhoneCountryChange = event => {
    const { value } = event.target;
    setValues(prev => ({ ...prev, phoneCountry: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting) return;

    const trimmedValues = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value])
    );

    const {
      password,
      confirmPassword,
      companyType,
      firstName,
      lastName,
      email,
      phone,
      phoneCountry,
      company,
      taxId,
      referralCode
    } = trimmedValues;
    const matchesExisting =
      selectedCompany &&
      trimmedValues.company.toLowerCase() === (selectedCompany.name || "").toLowerCase();
    const requiredValues = matchesExisting
      ? [firstName, lastName, email, phone, company, password, confirmPassword]
      : [firstName, lastName, email, phone, company, companyType, password, confirmPassword];

    if (requiredValues.some(value => !value)) {
      setError("Por favor completa todos los campos antes de continuar.");
      return;
    }

    if (!matchesExisting && !companyType) {
      setError("Selecciona el tipo de empresa para continuar.");
      return;
    }

    if (password.length < 8) {
      setError("La contrase?a debe tener al menos 8 caracteres.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Las contrase?as no coinciden. Revisa los campos e intenta de nuevo.");
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      const typedReferralCode = referralCode || "";
      const normalizedReferralCode = typedReferralCode.trim() || referral?.code || "";
      const referralLink =
        normalizedReferralCode && referral?.code === normalizedReferralCode
          ? referral?.link || ""
          : normalizedReferralCode
          ? buildReferralLink(normalizedReferralCode)
          : "";
      const selectedPhoneCountry =
        phoneCountry || values.phoneCountry || PHONE_COUNTRY_OPTIONS[0].value;
      const formattedPhone = `${selectedPhoneCountry}${phone.replace(/[\s-]/g, "")}`;
      await registerUserAccount({
        role: "provider",
        firstName,
        lastName,
        email,
        phone: formattedPhone,
        company,
        password,
        companyTaxId: taxId,
        existingCompanyId: matchesExisting ? selectedCompany.id : "",
        companyCategory: matchesExisting ? "" : companyType,
        isCompanyOwner,
        referralCode: normalizedReferralCode,
        referralLink
      });
      clearReferral();
      setValues(buildInitialState());
      setSelectedCompany(null);
      setIsCompanyOwner(false);
      navigate("/welcome", { replace: true });
    } catch (registrationError) {
      const message =
        registrationError?.message || "No pudimos crear la cuenta en este momento. Intenta nuevamente.";
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <main className="register-page">
      <div className="register-card">
        <div className="register-card__brand register-card__brand--provider">
          <p className="register-card__logo">FLOTTE</p>
          <div>
            <h2>Conecta tu flota con demanda real.</h2>
            <p>
              Activa campañas, responde requerimientos y cierra oportunidades con clientes verificados en toda LATAM.
            </p>
          </div>
          <ul className="register-card__list">
            <li>
              <FiTruck aria-hidden="true" />
              Publica unidades y servicios destacados.
            </li>
            <li>
              <FiLayers aria-hidden="true" />
              Gestiona solicitudes 24/7 desde un panel único.
            </li>
          </ul>
          <div className="register-card__badge">
            <FiShield aria-hidden="true" />
            <span>+2,000 cargas activas cada mes</span>
          </div>
        </div>
        <div className="register-card__form">
          <Link to="/register" className="register-card-back">
            <FiArrowLeft aria-hidden="true" />
            Volver
          </Link>
          <header className="register-form-header">
            <p className="register-form-eyebrow">Proveedores Flotte</p>
            <h1>Crea tu cuenta de Transportista</h1>
            <p>Completa la información para crear tu cuenta corporativa.</p>
          </header>

          {error ? (
            <div className="register-form-error" role="alert">
              {error}
            </div>
          ) : null}

          <div className="register-social">
            <button
              type="button"
              className="register-google-btn"
              onClick={onGoogleSignIn}
              disabled={!onGoogleSignIn || googleSignInPending || submitting}
            >
              <FcGoogle aria-hidden="true" />
              <span>{googleSignInPending ? "Conectando con Google…" : "Continuar con Google"}</span>
            </button>
            <div className="register-social-divider" role="separator" aria-label="O completa el formulario">
              <span>o completa el formulario</span>
            </div>
          </div>

          <form className="register-form" onSubmit={handleSubmit} noValidate>
            <section className="register-form-section">
              <p className="register-section-label">Datos del representante</p>
              <div className="register-field-grid register-field-grid--dense">
                <div className="register-field">
                  <label className="register-field-label" htmlFor="provider-first-name">
                    Nombre
                  </label>
                  <input
                    id="provider-first-name"
                    type="text"
                    name="firstName"
                    placeholder="Nombre"
                    value={values.firstName}
                    onChange={handleFieldChange("firstName")}
                    autoComplete="given-name"
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="register-field">
                  <label className="register-field-label" htmlFor="provider-last-name">
                    Apellido
                  </label>
                  <input
                    id="provider-last-name"
                    type="text"
                    name="lastName"
                    placeholder="Apellido"
                    value={values.lastName}
                    onChange={handleFieldChange("lastName")}
                    autoComplete="family-name"
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="register-field register-field--full">
                  <label className="register-field-label" htmlFor="provider-email">
                    Correo electrónico
                  </label>
                  <input
                    id="provider-email"
                    type="email"
                    name="email"
                    placeholder="nombre@empresa.com"
                    value={values.email}
                    onChange={handleFieldChange("email")}
                    autoComplete="email"
                    required
                    disabled={submitting}
                  />
                  <span className="register-field-helper">
                    Usaremos este correo para confirmar tu acceso y enviarte notificaciones clave.
                  </span>
                </div>
                <div className="register-field">
                  <label className="register-field-label" htmlFor="provider-phone">
                    Número de teléfono
                  </label>
                  <div className="register-phone-input">
                    <select
                      aria-label="Prefijo del país"
                      value={values.phoneCountry}
                      onChange={handlePhoneCountryChange}
                      disabled={submitting}
                    >
                      {PHONE_COUNTRY_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <input
                      id="provider-phone"
                      type="tel"
                      name="phone"
                      placeholder="55 1234 5678"
                      value={values.phone}
                      onChange={handleFieldChange("phone")}
                      autoComplete="tel"
                      pattern="[0-9\\s-]{7,20}"
                      required
                      disabled={submitting}
                    />
                  </div>
                  <span className="register-field-helper">
                    Ingresa solo los dígitos; el prefijo se selecciona automáticamente.
                  </span>
                </div>
                <div className="register-field">
                  <label className="register-field-label" htmlFor="provider-tax-id">
                    RFC (opcional)
                  </label>
                  <input
                    id="provider-tax-id"
                    type="text"
                    name="taxId"
                    placeholder="RFC"
                    value={values.taxId}
                    onChange={handleFieldChange("taxId")}
                    autoComplete="off"
                    disabled={submitting}
                  />
                </div>
              </div>
            </section>

            <section className="register-form-section">
              <p className="register-section-label">Datos de la empresa</p>
              <div className="register-field-grid register-field-grid--dense">
                <div className="register-field register-field--full">
                  <label className="register-field-label" htmlFor="provider-company">
                    Nombre de la empresa
                  </label>
                  <input
                    id="provider-company"
                    type="text"
                    name="company"
                    placeholder="Transportes Ejemplo SA de CV"
                    value={values.company}
                    onChange={handleFieldChange("company")}
                    autoComplete="organization"
                    required
                    disabled={submitting}
                  />
                  {selectedCompany ? (
                    <div className="register-company-selected" role="note">
                      <p>
                        Solicitarás incorporarte a <strong>{selectedCompany.name}</strong>
                        {selectedCompany.location ? ` (${selectedCompany.location})` : ""}. El administrador
                        de la empresa recibirá tu solicitud para aprobar tu acceso al panel.
                      </p>
                      <button
                        type="button"
                        className="register-company-selected__action"
                        onClick={handleClearSelectedCompany}
                        disabled={submitting}
                      >
                        Elegir otra empresa
                      </button>
                    </div>
                  ) : null}
                  {showSuggestions ? (
                    <div className="register-company-suggestions" role="listbox" aria-label="Empresas registradas">
                      {loading ? (
                        <div className="register-company-suggestions__loading">Buscando empresas...</div>
                      ) : suggestions.length > 0 ? (
                        suggestions.map(option => (
                          <button
                            key={option.id}
                            type="button"
                            className="register-company-suggestion"
                            onClick={() => handleSelectCompany(option)}
                            role="option"
                            aria-selected="false"
                          >
                            <span className="register-company-suggestion__name">{option.name}</span>
                            {option.location ? (
                              <span className="register-company-suggestion__location">{option.location}</span>
                            ) : null}
                          </button>
                        ))
                      ) : trimmedCompany.length >= 3 ? (
                        <div className="register-company-suggestions__empty">
                          No encontramos coincidencias. Escribe el nombre completo para registrar una nueva empresa.
                        </div>
                      ) : null}
                    </div>
                ) : null}
              </div>

              {!selectedCompany ? (
                <div className="register-field">
                    <label className="register-field-label" htmlFor="provider-company-type">
                      Tipo de empresa
                    </label>
                    <select
                      id="provider-company-type"
                      name="companyType"
                      value={values.companyType}
                      onChange={handleFieldChange("companyType")}
                      disabled={submitting}
                      required
                    >
                      <option value="" disabled>
                        Selecciona una opción
                      </option>
                      {COMPANY_TYPE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span className="register-field-helper">
                      Escoge el servicio principal que ofrece tu empresa para mostrarlo a los clientes adecuados.
                    </span>
                </div>
              ) : null}
            </div>
            <label className="register-owner-checkbox">
              <input
                type="checkbox"
                checked={isCompanyOwner}
                onChange={event => setIsCompanyOwner(event.target.checked)}
              />
              <span>Soy dueño / administrador de la empresa</span>
            </label>

            </section>

            <section className="register-form-section">
              <p className="register-section-label">Acceso</p>
              <div className="register-field-grid register-field-grid--dense">
                <div className="register-field">
                  <label className="register-field-label" htmlFor="provider-password">
                    Contraseña
                  </label>
                  <input
                    id="provider-password"
                    type="password"
                    name="password"
                    placeholder="********"
                    value={values.password}
                    onChange={handleFieldChange("password")}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    disabled={submitting}
                  />
                  <span className="register-field-helper">
                    Mínimo 8 caracteres. Recomendamos combinar mayúsculas, números y símbolos.
                  </span>
                </div>

                <div className="register-field">
                  <label className="register-field-label" htmlFor="provider-confirm-password">
                    Confirmar contraseña
                  </label>
                  <input
                    id="provider-confirm-password"
                    type="password"
                    name="confirmPassword"
                    placeholder="********"
                    value={values.confirmPassword}
                    onChange={handleFieldChange("confirmPassword")}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    disabled={submitting}
                  />
                  <span className="register-field-helper">
                    Vuelve a escribir la contraseña para asegurarte de que sea correcta.
                  </span>
                </div>
              </div>
            </section>

            <section className="register-form-section">
              <p className="register-section-label">Referidos</p>
              <div className="register-field register-field--full">
                <label className="register-field-label" htmlFor="provider-referral-code">
                  Código de referido (opcional)
                </label>
                <input
                  id="provider-referral-code"
                  type="text"
                  name="referralCode"
                  placeholder="MIEMPRESA"
                  value={values.referralCode}
                  onChange={handleFieldChange("referralCode")}
                  autoComplete="off"
                  disabled={submitting}
                  maxLength={14}
                />
                <span className="register-field-helper">
                  Escribe el código que recibiste para que tú y tu contacto reciban 120 créditos.
                </span>
              </div>
            </section>

            <div className="register-form-actions">
              <button type="submit" className="register-submit-btn" disabled={submitting}>
                {submitting ? "Creando cuenta..." : "Crear cuenta"}
              </button>
              <p className="register-form-note">
                ¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
};

export default RegisterProvider;
